import { useEffect, useState } from 'react';
import useGameStore, { acceptThresholdOf, describeDealMeter, tradeShortfall } from '../store/gameStore';
import type { Player, PropertyTile, TradeOffer, TradeShareLot } from '../types/gameTypes';
import { money } from '../lib/format';
import { DISTRICT_META } from './districtMeta';

const unitOf = (tile: PropertyTile) => (tile.valuation > 0 ? tile.valuation : tile.baseCost) / Math.max(1, tile.totalShares);
/** Clamp an arbitrary typed cash amount into [0, available]. */
const clamp = (value: number, max: number) => Math.max(0, Math.min(Math.floor(Number.isFinite(value) ? value : 0), Math.max(0, Math.floor(max))));

export const TradeModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const store = useGameStore();
  const { board, players, currentPlayerIndex, tradeOffer } = store;
  // Hotseat: the deal builder belongs to the human currently at the controls.
  const active = players[currentPlayerIndex];
  const human = active && !active.isAI ? active : players[0];
  const [targetId, setTargetId] = useState<string | null>(null);
  const [offerCash, setOfferCash] = useState(0);
  const [offerLots, setOfferLots] = useState<TradeShareLot[]>([]);
  const [requestCash, setRequestCash] = useState(0);
  const [requestLots, setRequestLots] = useState<TradeShareLot[]>([]);
  const [sentId, setSentId] = useState('');
  const [verdict, setVerdict] = useState<{ accepted: boolean; name: string; note: string; shortfall: number } | null>(null);

  useEffect(() => {
    setOfferCash(0); setRequestCash(0); setOfferLots([]); setRequestLots([]); setVerdict(null); setSentId('');
  }, [targetId, open]);
  useEffect(() => {
    if (!open) return;
    useGameStore.getState().beginTradeNegotiation();
    return () => { useGameStore.getState().endTradeNegotiation(); };
  }, [open]);
  // Closing the builder also drops a settled bot verdict (never a PENDING offer),
  // so a stale rejection can never gate the game loop.
  const closeModal = () => { store.dismissTradeVerdict(); onClose(); };

  // Watch the store's verdict for the proposal we just sent. The modal STAYS OPEN and
  // shows the bot's actual strategic reasoning, plus the cash shortfall on a decline.
  useEffect(() => {
    if (!open || !tradeOffer || tradeOffer.id !== sentId || tradeOffer.status === 'PENDING') return;
    const target = players.find((p) => p.id === tradeOffer.receiverId);
    const shortfall = tradeOffer.status === 'REJECTED' ? tradeShortfall(board, players, tradeOffer) : 0;
    setVerdict({
      accepted: tradeOffer.status === 'ACCEPTED',
      name: target?.name ?? 'Rival',
      note: tradeOffer.verdictNote ?? (tradeOffer.status === 'ACCEPTED' ? 'Přijato.' : 'Odmítnuto.'),
      shortfall,
    });
  }, [tradeOffer, sentId, open, board, players]);

  if (!open || !human) return null;
  const target = players.find((p) => p.id === targetId);
  const humanHoldings = board.filter((t) => (t.shares[human.id] ?? 0) > 0);
  const targetHoldings = board.filter((t) => (t.shares[target?.id ?? ''] ?? 0) > 0);
  const offer: TradeOffer = {
    id: `trade-${Date.now()}`,
    senderId: human.id,
    receiverId: target?.id ?? '',
    offeredCash: offerCash,
    offeredShares: offerLots.filter((l) => l.shares > 0),
    requestedCash: requestCash,
    requestedShares: requestLots.filter((l) => l.shares > 0),
    status: 'PENDING',
  };
  const meter = describeDealMeter(board, offer);
  const meterPct = Math.max(6, Math.min(100, Math.round((meter.ratio / 1.3) * 100)));
  const hasAssets = meter.offered > 0 || meter.requested > 0;
  // Sentiment tier — meter.ratio = offeredValue / requestedValue = the bot's advantage
  // (ratio ≥ 1 ⇒ strictly profitable for the receiver). Green ≥ 1.15× (≥1.05× for an
  // AGGRESSIVE counterpart), yellow 0.95×–1.15×, red < 0.95×.
  const greenThreshold = target ? acceptThresholdOf(target.aiArchetype) : 1.15;
  const tier: 'green' | 'yellow' | 'red' = meter.ratio >= greenThreshold ? 'green' : meter.ratio >= 0.95 ? 'yellow' : 'red';
  const tierBar = tier === 'green' ? 'bg-emerald-400' : tier === 'yellow' ? 'bg-amber-400' : 'bg-rose-400';
  const tierPill = tier === 'green' ? 'bg-emerald-500/15 text-emerald-300' : tier === 'yellow' ? 'bg-amber-500/15 text-amber-300' : 'bg-rose-500/15 text-rose-300';
  const tierText =
    tier === 'green' ? '🟢 Favorable — Bot will readily accept this deal!'
    : tier === 'yellow' ? '🟡 Fair Trade — Bot is likely to accept.'
    : '🔴 Unfavorable — Bot will decline (needs more cash or equity to balance).';
  const setter = (list: TradeShareLot[], tileId: string, shares: number): TradeShareLot[] =>
    [...list.filter((l) => l.tileId !== tileId), ...(shares > 0 ? [{ tileId, shares }] : [])];
  const lotOf = (list: TradeShareLot[], tileId: string) => list.find((l) => l.tileId === tileId)?.shares ?? 0;
  const CashButtons = ({ cash, setCash, cashMax }: { cash: number; setCash: (v: number) => void; cashMax: number }) => (
    <div className="mt-1.5 space-y-1.5">
      <div className="flex flex-wrap gap-1.5">
        {[50, 200, 500].map((inc) => (
          <button key={inc} disabled={cash + inc > cashMax} onClick={() => setCash(clamp(cash + inc, cashMax))}
            className="rounded-lg bg-slate-800 px-2.5 py-1 text-[11px] font-black text-cyan-200 transition hover:bg-slate-700 disabled:opacity-30 disabled:text-slate-500">
            +${inc}
          </button>
        ))}
        <button onClick={() => setCash(0)} disabled={cash === 0} className="rounded-lg bg-slate-800 px-2.5 py-1 text-[11px] font-black text-rose-300 transition hover:bg-slate-700 disabled:opacity-30">Clear</button>
        <span className="ml-auto self-center text-[11px] font-bold text-slate-400">Selected: <b className="text-slate-100">{money(cash)}</b> / {money(cashMax)}</span>
      </div>
      {/* Exact amount: type any value; clamped to [0, available cash] on change and blur. */}
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-bold text-slate-400">Přesná částka</span>
        <span className="text-[11px] font-black text-slate-500">$</span>
        <input
          type="number"
          min={0}
          max={cashMax}
          step={1}
          value={cash === 0 ? '' : cash}
          placeholder="např. 347"
          onChange={(e) => setCash(clamp(Number(e.target.value), cashMax))}
          onBlur={(e) => setCash(clamp(Number(e.target.value), cashMax))}
          className="w-28 rounded-lg border border-slate-600 bg-slate-900 px-2 py-1 text-right text-xs font-bold text-slate-100 outline-none focus:border-cyan-400"
        />
        <button onClick={() => setCash(cashMax)} className="rounded-lg bg-slate-800 px-2 py-1 text-[10px] font-black text-amber-200 hover:bg-slate-700">Max</button>
      </div>
    </div>
  );

  const AssetGrid = ({ tiles, owner, lots, setLots, tone }: {
    tiles: PropertyTile[]; owner: Player; lots: TradeShareLot[]; setLots: (v: TradeShareLot[]) => void; tone: string;
  }) => (
    <div className="mt-2 max-h-52 space-y-2 overflow-auto pr-1">
      {tiles.length === 0 && <p className="text-xs text-slate-500">No holdings — nothing to trade.</p>}
      {tiles.map((tile) => {
        const owned = tile.shares[owner.id] ?? 0;
        const qty = lotOf(lots, tile.id);
        const selected = qty > 0;
        const meta = DISTRICT_META[tile.district];
        const toggle = () => setLots(selected ? setter(lots, tile.id, 0) : setter(lots, tile.id, Math.max(1, owned)));
        return (
          <div key={tile.id} role="button" tabIndex={0} onClick={toggle}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggle(); }}
            className={`cursor-pointer rounded-xl border-2 p-2 transition ${selected ? 'border-emerald-400 bg-emerald-500/10 shadow-[0_0_12px_rgba(52,211,153,0.25)]' : 'border-slate-700/60 bg-slate-950/60 hover:border-slate-500'}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="flex min-w-0 items-center gap-1.5 text-xs font-bold text-slate-100">
                <span className="h-3 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: meta.bar }} />
                <span className="truncate">{tile.name}</span>
              </p>
              <span className="shrink-0 text-[10px] font-bold text-slate-400">{owned} owned · {money(unitOf(tile))}/sh</span>
            </div>
            {selected && (
              <div className="mt-1.5 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button onClick={() => setLots(setter(lots, tile.id, Math.max(0, qty - 1)))} className="h-7 w-7 rounded-lg bg-slate-800 text-sm font-black text-slate-100 hover:bg-slate-700">−</button>
                <input type="range" min={0} max={owned} value={qty} onChange={(e) => setLots(setter(lots, tile.id, Number(e.target.value)))} className={`h-1.5 flex-1 ${tone === 'cyan' ? 'accent-cyan-400' : 'accent-violet-400'}`} />
                <button onClick={() => setLots(setter(lots, tile.id, Math.min(owned, qty + 1)))} className="h-7 w-7 rounded-lg bg-slate-800 text-sm font-black text-slate-100 hover:bg-slate-700">+</button>
                <span className="w-24 text-right text-[11px] font-bold text-emerald-300">{qty} sh ≈ {money(qty * unitOf(tile))}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const Desk = ({ title, subtitle, tiles, owner, lots, setLots, cash, setCash, cashMax, tone, accent }: {
    title: string; subtitle: string; tiles: PropertyTile[]; owner: Player; lots: TradeShareLot[]; setLots: (v: TradeShareLot[]) => void;
    cash: number; setCash: (v: number) => void; cashMax: number; tone: 'cyan' | 'violet'; accent: string;
  }) => (
    <div className={`flex-1 rounded-2xl border-2 bg-slate-900/70 p-3 ${tone === 'cyan' ? 'border-cyan-500/50' : 'border-violet-500/50'}`}>
      <p className={`text-[11px] font-black uppercase tracking-widest ${accent}`}>{title}</p>
      <p className="text-[10px] font-semibold text-slate-500">{subtitle}</p>
      <p className="mt-2 text-xs text-slate-400">Cash: <b className="text-slate-100">{money(cash)}</b> / {money(cashMax)} available</p>
      <CashButtons cash={cash} setCash={setCash} cashMax={cashMax} />
      <AssetGrid tiles={tiles} owner={owner} lots={lots} setLots={setLots} tone={tone} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/80 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={closeModal}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl border-2 border-cyan-500/50 bg-slate-900 p-5 pb-safe shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-extrabold text-white">🤝 Bilateral P2P Trade</h3>
          <button onClick={closeModal} className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300">Close / ESC</button>
        </div>
        {/* STEP 1 — Select Opponent: large interactive player cards */}
        {!target && (
          <div className="mt-4">
            <p className="text-sm font-bold text-slate-300">Select a trading partner:</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {players.filter((p) => p.id !== human.id && !p.isBankrupt).map((p) => {
                const holdings = board.filter((t) => (t.shares[p.id] ?? 0) > 0).slice(0, 4);
                return (
                  <button key={p.id} onClick={() => setTargetId(p.id)}
                    className="rounded-2xl border-2 border-slate-700 bg-slate-950/70 p-4 text-left transition hover:border-cyan-300 hover:bg-slate-800/70">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full text-lg font-black text-slate-950" style={{ backgroundColor: p.color }}>
                        {p.name.charAt(0)}
                      </span>
                      <span>
                        <p className="text-sm font-black text-white">{p.name}{p.isAI ? ' 🤖' : ' 🧑'}</p>
                        <p className="text-[11px] font-bold text-emerald-300">{money(p.cash)} cash</p>
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {holdings.length === 0 && <span className="text-[10px] text-slate-500">No properties yet</span>}
                      {holdings.map((t) => (
                        <span key={t.id} className="rounded-md border px-1.5 py-0.5 text-[9px] font-bold text-slate-200" style={{ borderColor: DISTRICT_META[t.district].bar }}>
                          {t.name} · {t.shares[p.id]} sh
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 2 — Visual Trading Desk: side-by-side offer builder */}
        {target && (
          <>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => setTargetId(null)} className="rounded-lg bg-slate-800 px-2.5 py-1 text-xs font-bold text-slate-300 hover:bg-slate-700">← Change partner</button>
              <span className="flex items-center gap-1.5 text-sm font-bold text-slate-100">
                <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: target.color }} />
                Trading with {target.name}
              </span>
            </div>
            <div className="mt-3 flex flex-col gap-3 md:flex-row">
              <Desk title="You Offer" subtitle="Co dáváš ty" tiles={humanHoldings} owner={human} lots={offerLots} setLots={setOfferLots} cash={offerCash} setCash={setOfferCash} cashMax={human.cash} tone="cyan" accent="text-cyan-300" />
              <Desk title="They Give" subtitle="Co požaduješ" tiles={targetHoldings} owner={target} lots={requestLots} setLots={setRequestLots} cash={requestCash} setCash={setRequestCash} cashMax={target.cash} tone="violet" accent="text-violet-300" />
            </div>
            {/* Deal Barometer + sentiment pill (same evaluator the bot uses) */}
            <div className="mt-3 rounded-2xl border border-slate-700/60 bg-slate-950/60 p-3 text-xs">
              <div className="flex justify-between"><span className="text-slate-400">Offered value</span><b className="text-cyan-200">{money(meter.offered)}</b></div>
              <div className="flex justify-between"><span className="text-slate-400">Requested value</span><b className="text-violet-200">{money(meter.requested)}</b></div>
              <div className="flex justify-between"><span className="text-slate-400">Ratio (for them)</span><b className="text-slate-100">{meter.ratio.toFixed(2)}×</b></div>
              <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-800">
                <div className={`h-full ${tierBar}`} style={{ width: `${meterPct}%` }} />
              </div>
            </div>
            <p className={`mt-2 rounded-xl px-3 py-2 text-center text-sm font-black ${tierPill}`}>
              {tierText}
            </p>

            {/* Contextual reasoning: the bot's actual strategic thought, and the shortfall to fix. */}
            {verdict && (
              <div className={`mt-2 flex items-start gap-2 rounded-2xl border-2 p-3 ${verdict.accepted ? 'border-emerald-300/70 bg-emerald-500/10' : 'border-amber-300/70 bg-amber-500/10'}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-base ${verdict.accepted ? 'bg-emerald-300' : 'bg-amber-300'}`}>
                  {verdict.accepted ? '💬' : '🚫'}
                </span>
                <div className="min-w-0">
                  <p className={`text-sm font-black ${verdict.accepted ? 'text-emerald-100' : 'text-amber-100'}`}>
                    {verdict.name}: „{verdict.note}“
                  </p>
                  {!verdict.accepted && verdict.shortfall > 0 && (
                    <p className="mt-1 text-xs font-bold text-amber-200">
                      💡 Zkus přidat kolem {money(verdict.shortfall)} v hotovosti a pošli znovu.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Unambiguous big CTA */}
            <button
              disabled={!hasAssets}
              onClick={() => { setSentId(offer.id); setVerdict(null); store.dismissTradeVerdict(); store.proposeTrade(offer); }}
              className="mt-3 min-h-[56px] w-full rounded-2xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-400 to-teal-400 px-5 py-3 text-base font-black text-slate-950 hover:from-emerald-300 hover:to-teal-300 disabled:opacity-40"
            >
              🤝 SEND PROPOSAL
            </button>
            {!hasAssets && <p className="mt-1 text-center text-[11px] font-semibold text-slate-500">Select at least one asset on either side to propose a deal.</p>}
          </>
        )}
      </div>
    </div>
  );
};

const AttentionModal = () => {
    const attention = useGameStore((s) => s.attention);
    const enterAuction = useGameStore((s) => s.enterStagedAuction);
    const acknowledge = useGameStore((s) => s.acknowledgeAttention);
    const respond = useGameStore((s) => s.respondToTrade);
    const tradeOffer = useGameStore((s) => s.tradeOffer);
    const takeover = useGameStore((s) => s.executeTakeover);
    if (!attention) return null;
    const stakeDeal = attention.kind === 'TRADE' ? tradeOffer : null;
    return (
      <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm">
        <div className="attention-splash w-full max-w-lg overflow-hidden rounded-2xl border-4 border-amber-300 bg-gradient-to-b from-amber-950 via-slate-900 to-slate-900 shadow-[0_0_70px_rgba(251,191,36,0.55)]">
          <div className="bg-gradient-to-r from-amber-400 to-orange-500 px-5 py-3">
            <p className="text-lg font-black text-slate-950">⚠️ ATTENTION REQUIRED</p>
          </div>
          <div className="p-5">
            <p className="text-sm font-bold text-amber-200">{attention.title.replace('⚠️ ATTENTION REQUIRED: ', '')}</p>
            <p className="mt-2 text-sm leading-relaxed text-slate-200">{attention.body}</p>
            {(() => {
              // Plain-language trade summary (set-collection deals): who gives what to whom.
              const sum = attention.tradeSummary;
              if (!sum) return null;
              return (
                <div className="mt-3 rounded-xl border border-amber-300/30 bg-slate-950/60 p-3 text-sm">
                  <p className="font-black text-amber-200">{sum.fromName} ti nabízí:</p>
                  {sum.give.map((l, i) => <p key={`g${i}`} className="text-emerald-300">→ {l}</p>)}
                  <p className="mt-2 font-black text-amber-200">Výměnou požaduje:</p>
                  {sum.take.map((l, i) => <p key={`t${i}`} className="text-rose-300">→ {l}</p>)}
                  <p className="mt-2 text-xs font-semibold text-slate-300">{sum.note}</p>
                </div>
              );
            })()}
            {(attention.stake?.length ?? 0) > 0 && (
              <dl className="mt-3 rounded-xl border border-amber-300/30 bg-slate-950/60 p-3">
                <p className="text-[11px] font-black uppercase tracking-widest text-amber-200/90">What is at stake</p>
                {attention.stake!.map((row) => (
                  <div key={row.label} className="mt-1 flex items-start justify-between gap-3 text-xs">
                    <dt className="text-slate-400">{row.label}</dt>
                    <dd className="text-right font-bold text-slate-100">{row.value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {stakeDeal && (
              <p className="mt-3 text-xs text-slate-300">
                Deal meter: <b className="text-amber-200">{stakeDeal.offeredCash > 0 || stakeDeal.requestedCash > 0 ? `${money(stakeDeal.offeredCash)} offered / ${money(stakeDeal.requestedCash)} requested` : 'shares for shares'}</b> · status <b>{stakeDeal.status}</b>
              </p>
            )}
            <div className="mt-5 grid gap-2">
              {attention.kind === 'AUCTION' && (
                <button onClick={enterAuction} className="min-h-[56px] w-full rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-3 text-base font-black text-slate-950 hover:from-amber-300 hover:to-orange-300">
                  🔨 Enter Auction / Take Action
                </button>
              )}
              {attention.kind === 'TRADE' && (
                <>
                  <button onClick={() => respond(true)} className="min-h-[56px] w-full rounded-2xl border-2 border-emerald-400 bg-gradient-to-r from-emerald-500 to-teal-400 px-5 py-3 text-base font-black text-slate-950">🤝 Přijmout obchod</button>
                  <button onClick={() => respond(false)} className="min-h-[56px] w-full rounded-2xl border-2 border-rose-500 bg-slate-800 px-5 py-3 text-base font-extrabold text-rose-200 hover:bg-slate-700">❌ Odmítnout</button>
                </>
              )}
              {attention.kind === 'TAKEOVER' && (
                <button onClick={() => { const buyerId = attention.buyerId; const tileId = attention.tileId; acknowledge(); if (buyerId && tileId) takeover(buyerId, tileId); }} className="min-h-[56px] w-full rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-3 text-base font-black text-slate-950">
                  ⚔️ Allow Takeover & Collect Payout
                </button>
              )}
              {attention.kind === 'VOTE' && (
                <button onClick={acknowledge} className="min-h-[56px] w-full rounded-2xl border-2 border-amber-300 bg-gradient-to-r from-amber-400 to-orange-400 px-5 py-3 text-base font-black text-slate-950">
                  🗳️ Acknowledge — I'll Vote in Governance
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
};

/** Hotseat human→human review: "hand controls to Player 2" splash. */
export const HotseatTradeReview = () => {
  const store = useGameStore();
  const { hotseatReview, board, players } = store;
  if (!hotseatReview) return null;
  const sender = players.find((p) => p.id === hotseatReview.senderId);
  const receiver = players.find((p) => p.id === hotseatReview.receiverId);
  if (!sender || !receiver) return null;
  const nameOf = (tileId: string) => board.find((t) => t.id === tileId)?.name ?? tileId;
  const lots = (list: TradeShareLot[], label: string) =>
    list.map((lot) => `${lot.shares} × ${nameOf(lot.tileId)} → ${label}`).join(', ');
  const giveLines = [
    hotseatReview.offeredCash > 0 ? `${sender.name} pays ${money(hotseatReview.offeredCash)} cash` : null,
    lots(hotseatReview.offeredShares, receiver.name),
  ].filter(Boolean) as string[];
  const takeLines = [
    hotseatReview.requestedCash > 0 ? `${receiver.name} pays ${money(hotseatReview.requestedCash)} cash` : null,
    lots(hotseatReview.requestedShares, sender.name),
  ].filter(Boolean) as string[];
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-950/90 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border-4 border-violet-400/70 bg-slate-900 shadow-2xl" style={{ borderColor: receiver.color }}>
        <div className="px-5 py-4" style={{ backgroundColor: receiver.color }}>
          <p className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-950/80">Hand the controls over</p>
          <h2 className="text-xl font-black text-slate-950">Review Offer: {receiver.name}</h2>
        </div>
        <div className="p-5">
          <p className="text-xs text-slate-400">{sender.name} built this bilateral deal for you to review:</p>
          <div className="mt-3 grid gap-2 text-sm">
            <div className="rounded-xl border border-cyan-500/40 bg-cyan-500/10 p-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300">You receive</p>
              {giveLines.length === 0 ? <p className="text-xs text-slate-500">Nothing.</p> : giveLines.map((l, i) => <p key={i} className="text-slate-200">{l}</p>)}
            </div>
            <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-rose-300">You give</p>
              {takeLines.length === 0 ? <p className="text-xs text-slate-500">Nothing.</p> : takeLines.map((l, i) => <p key={i} className="text-slate-200">{l}</p>)}
            </div>
          </div>
          <div className="mt-5 grid gap-2">
            <button
              onClick={() => store.respondHotseatTrade(true)}
              className="min-h-[56px] w-full rounded-2xl border-2 border-emerald-400 bg-gradient-to-r from-emerald-500 to-teal-400 px-5 py-3 text-base font-black text-slate-950 hover:from-emerald-400 hover:to-teal-300"
            >
              ✅ Accept Deal
            </button>
            <button
              onClick={() => store.respondHotseatTrade(false)}
              className="min-h-[56px] w-full rounded-2xl border-2 border-rose-500 bg-slate-800 px-5 py-3 text-base font-extrabold text-rose-200 hover:bg-slate-700"
            >
              ❌ Decline / Counter Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { AttentionModal };
